/**
 * ToolbarButton — Premium toolbar button with built-in 3D press,
 * icon nudge, and tooltip. Used across Radar, PitchDeck, and other
 * pages for consistent interaction effects.
 *
 * Usage:
 *   <ToolbarButton
 *     icon={<Play size={14} />}
 *     label="Auto-rotate"
 *     tooltip="Start orbit animation"
 *     onClick={handleOrbit}
 *     active={isOrbiting}
 *   />
 */
import * as React from "react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { fx } from "@/lib/effects";

interface ToolbarButtonProps {
  icon: React.ReactNode;
  label?: string;
  tooltip?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  "aria-pressed"?: boolean;
  "aria-expanded"?: boolean;
  size?: "sm" | "md";
}

export function ToolbarButton({
  icon,
  label,
  tooltip,
  onClick,
  active = false,
  disabled = false,
  className,
  size = "sm",
  ...ariaProps
}: ToolbarButtonProps) {
  const sizeClasses = size === "sm"
    ? "h-7 px-2 text-xs gap-1"
    : "h-8 px-2.5 text-xs gap-1.5";

  const btn = (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium select-none whitespace-nowrap",
        "border bg-transparent",
        "focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none",
        fx.toolbar,
        sizeClasses,
        active
          ? "bg-primary/10 border-primary/40 text-primary shadow-[0_0_8px_1px_rgba(71,130,95,0.15)]"
          : "border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        disabled && "opacity-50 pointer-events-none",
        className
      )}
      {...ariaProps}
    >
      {icon}
      {label && <span className="hidden sm:inline">{label}</span>}
    </button>
  );

  if (!tooltip) return btn;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
